import {Detail, Icon, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import novaMark from '../../../../../../images/nova/nova-mark.svg';
import {getAvailableAgents} from '../../../nova-invocation/novaInvocationStore';
import {FailurePresentation} from '../../../nova-invocation/FailurePresentation';
import {BasedOnList} from '../../../nova-invocation/BasedOnList';
import {NovaCompactButton} from '../../../nova-invocation/NovaCompactButton';
import {RunTimestamp} from './RunTimestamp';
import {RoutingDisclosure} from '../../../nova-invocation/RoutingDisclosure';
import {basedOnItemsFor, isAnswered, outcomeFor} from './novaThreadTurn';

export interface NovaConversationTurnProps {
	run: {
		id: string;
		// Which conversation this run belongs to. Runs sharing a thread are one conversation; a new
		// thread is a new one, which is how changing the agent retires what came before it.
		threadId: string | null;
		agentId: string | null;
		status: string | null;
		outcome: string | null;
		answer: string | null;
		userMessageText: string | null;
		ranAt: string | null;
		failureState: string | null;
		grounding: {resolved?: ReadonlyArray<{token: string | null; scope: string | null}> | null} | null;
	};
	// Whether this turn shows what the run read. Only the conversation's opening turn does: the
	// sources are the same for every turn of a thread, and repeating them under each answer reads
	// as five separate reads of the same five things.
	showGrounding?: boolean;
	// The thread's own change-agent control, placed inside this card rather than built here. The
	// affordance owns an agent picker anchored to its own button, so it stays where it is mounted
	// and this component only decides where it sits.
	changeAgentAffordance?: React.ReactNode;
	// What the discard control should be called. A thread of one answer discards "Discard"; a
	// conversation with replies in it discards all of them, and says so.
	discardLabel?: string;
	// Shown, but refused, with the reason on it. Distinct from omitting onDiscard, which takes the
	// control away entirely: a person who may not act is told why, where a control that is simply
	// absent in this turn's position (every turn but the newest) has nothing to explain.
	discardDisabled?: boolean;
	discardDisabledReason?: string;
	// Offers this turn's own way to retire the whole conversation. Thread-scoped, not turn-scoped:
	// one discarded run retires every turn of the thread it belongs to, which is why a thread hands
	// this to one turn rather than to all of them. Optional and undefined by default, so a turn
	// rendered without it - a read-only task, a thread mid-run, every existing caller - shows no
	// discard control at all rather than one that cannot be honoured.
	onDiscard?: () => void;
	// Collapses the whole thread from this turn's own header. Offered on the opening turn only, as
	// the chevron beside the brand name - the same control the header itself reads as. Optional and
	// undefined by default, so a turn rendered without it (a later turn, or a thread whose collapse
	// is withheld for a reason of its own) simply has no chevron.
	onCollapse?: () => void;
	// Rendered as the thread's single summary row: the header alone, with the body and every control
	// under it folded away. The header is the same one the expanded turn carries, so the control that
	// folded the thread and the row reporting it folded are one object rather than two that have to
	// agree.
	collapsed?: boolean;
	// What the summary row says is hidden behind it. Supplied by the thread, which is the only place
	// that can count what it folded away.
	hiddenNote?: string;
	onExpand?: () => void;
	// What the person typed in reply, already separated from the prompt the run saved around it.
	// Decided by the thread, which is the only place that can see the opening run this one repeats.
	// Absent or null means this turn has no reply of its own to show.
	replyText?: string | null;
}

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

// The same blue triple NovaInvocationControl's chip uses, so the control that starts a run and the
// panel that shows its answer read as one feature rather than two.
const SURFACE = ref?.blue['50'] ?? '';
const SURFACE_BORDER = ref?.blue['100'] ?? '';
const ACCENT = ref?.blue['900'] ?? '';
const NEUTRAL_BORDER = sys?.border.decorative ?? '';
const CARD_RADIUS = size?.borderradius['3'] ?? '';
const CHIP_RADIUS = size?.borderradius['2'] ?? '';
// The reader's mark sits a few steps up the same blue scale the panel is drawn from, so the two
// sides of the exchange read as one conversation rather than two unrelated palettes.
const VIEWER_MARK_SURFACE = ref?.blue['300'] ?? '';
const VIEWER_MARK_GLYPH = sys?.text.content.ondark ?? '';
// The tag sits on the untinted card, so it carries the panel's own tint to stay legible as a label
// rather than disappearing into the card behind it.
const REPLY_TAG_SURFACE = ref?.blue['50'] ?? '';

// The tinted panel is the agent speaking. Everything around it in the modal is the author's own
// text, so the change of background does real work: it marks where authored text stops and
// generated text starts. The accent edge is what carries that at a glance down a long thread.
const AnswerCard = styled.div<{isCollapsed?: boolean}>`
	display: flex;
	flex-direction: column;
	gap: 10px;
	/* Takes the row's spare width, the same way the reply card beside it does. Without this the card
	   shrinks to its content, which a long answer hides and a one-line collapsed header does not:
	   the summary row would end wherever its text ended, and the hidden-count and Show that are
	   meant to sit at the far edge would have no width to be pushed across. */
	flex: 1;
	min-width: 0;
	/* Tighter when it carries a header and nothing else, so a folded thread reads as one row rather
	   than an empty card with a line in it. */
	padding: ${props => (props.isCollapsed ? '10px 15px' : '14px 16px')};
	background: ${SURFACE};
	border: 1px solid ${SURFACE_BORDER};
	border-left: 3px solid ${ACCENT};
	border-radius: ${CARD_RADIUS};
`;

// The reply the user sent, on the untinted side of the same contrast: a turn of the conversation,
// but authored rather than generated.
const QuestionCard = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	padding: 12px 14px;
	border: 1px solid ${NEUTRAL_BORDER};
	border-radius: ${CARD_RADIUS};
`;

// A reply is laid out exactly like an answer - avatar outside, card beside it - so the two sides of
// the conversation share one left edge. Rendered full-width instead, the reply started further left
// than every answer around it and the thread read as two ragged columns rather than one exchange.
const QuestionRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 11px;
`;

// Matches the answer's mark footprint so both cards begin at the same x, whichever side authored
// the turn.
// The reader's own mark beside a reply they sent. A glyph rather than their initials: this says
// which side of the conversation authored the turn, which is the only thing the thread needs to
// tell apart - the card's own header already names them. PersonInitials, the app's hexagon
// elsewhere, only takes initials, and the design system has no avatar of this shape at all, so the
// hexagon is cut here and filled from the same palette every other avatar in this modal uses.
const QuestionAvatar = styled.div`
	width: 30px;
	height: 32px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${VIEWER_MARK_SURFACE};
	clip-path: polygon(50% 0%, 100% 27%, 100% 73%, 50% 100%, 0% 73%, 0% 27%);

	svg {
		stroke: ${VIEWER_MARK_GLYPH};
	}
`;

const QuestionAvatarSlot = styled.div`
	width: 30px;
	flex-shrink: 0;
	display: flex;
	justify-content: center;
`;

const QuestionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	margin-bottom: 8px;
`;

const QuestionAuthor = styled.span`
	font-weight: 700;
`;

// Says which conversation the reply belongs to, in the same shape and palette as the agent chip on
// an answer - the two are the same kind of label, one per side of the exchange.
const ReplyTag = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 2px 6px;
	font-size: 10px;
	line-height: 14px;
	font-weight: 500;
	border-radius: ${CHIP_RADIUS};
	background: ${REPLY_TAG_SURFACE};
	color: ${ACCENT};
`;

const TurnStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const AnswerRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 11px;
`;

// The mark carries its own rounded gradient tile, so it needs no wrapper to sit on - only a fixed
// square footprint so the header beside it cannot squash it.
const NovaMarkImage = styled.img`
	width: 28px;
	height: 28px;
	flex-shrink: 0;
`;

const HeaderRow = styled.div<{isCollapsed?: boolean}>`
	display: flex;
	align-items: center;
	gap: 8px;
	/* Wraps while expanded, where the header can run long beside a narrow modal. Collapsed it must
	   not: the whole row is one line by definition, and a wrap would drop the hidden-count and Show
	   under the name instead of holding them at the far edge. */
	flex-wrap: ${props => (props.isCollapsed ? 'nowrap' : 'wrap')};
	/* Nothing follows the header once collapsed, so the gap under it would be trailing space inside
	   the card rather than separation from anything. */
	margin-bottom: ${props => (props.isCollapsed ? '0' : '10px')};
	${props => props.isCollapsed && 'cursor: pointer;'}
`;

// Pushed to the far edge, away from the name and agent that open the row: what is hidden is a
// property of the folded thread, not another attribute of the turn speaking.
const HiddenNote = styled.span`
	margin-left: auto;
	color: ${sys?.text.content.auxiliary ?? ''};
`;

// A plain text affordance rather than a design-system Button: the whole header already reads as the
// control, and a bordered button inside a one-line summary row would out-weigh the row it sits in.
const ShowAction = styled.span`
	font-weight: 500;
	color: ${ref?.blue['900'] ?? ''};
`;

// Who is speaking, before the agent that spoke for them. Heavier than the design system's own
// 12px style, which is its lightest weight: the name is the anchor of the header, and everything
// after it - the agent, the time - is an attribute of what it introduces.
const BrandName = styled.span`
	font-weight: 700;
`;

// The disclosure brings a flex row of its own, which would otherwise take the header's spare
// width and push everything after it to the far edge. The agent and the time belong beside the
// name, as one phrase.
const AgentSlot = styled.div`
	flex: none;
`;

// The chevron the whole header reads as: it turns to point at the collapsed state, so the control
// and the state it reports are the same object.
const CollapseToggle = styled.button<{isCollapsed?: boolean}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	flex: none;
	padding: 0;
	border: none;
	background: none;
	color: ${sys?.text.content.auxiliary ?? ''};
	cursor: pointer;
	/* Turns to point at the state it reports: down while the thread is open, right once folded. */
	transform: rotate(${props => (props.isCollapsed ? '-90deg' : '0deg')});
	transition: transform 0.15s;

	svg path {
		fill: ${sys?.text.content.auxiliary ?? ''};
	}

	&:focus-visible {
		outline: 2px solid ${sys?.border.focusring ?? ''};
		outline-offset: 1px;
	}
`;

// The change-agent control, and the actions, each sit a little clear of the body above them.
const CardRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
`;

// Discard sits at the far end of the action row, away from the actions that add something. It is
// the only control in the card that takes the conversation away.
const DiscardSlot = styled.div`
	margin-left: auto;
`;

// How long the copy control states that it copied before returning to offering the copy again.
// Long enough to be read, short enough that the control is back to its normal label before the
// person's next glance at it.
const COPIED_LABEL_MS = 1500;

// The answer is free text from a model, and the newlines it wrote are the whole of the formatting it
// has. pre-wrap keeps them without introducing a renderer that would then also have to be trusted
// with the content.
const AnswerText = styled.div`
	white-space: pre-wrap;
`;

// A run's outcome is only meaningful once the run has actually finished. Reading outcomeFor() on
// a run still in flight would tell a lie: novaThreadTurn's outcomeFor treats "not one of the three
// terminal values" as a generic failure (the honest reading for a *saved* record that ought to have
// a terminal outcome), but a running turn has no terminal outcome yet by design, not by accident —
// see the "verify state, do not infer it" rule this guard exists to satisfy. Gating on the outcome
// field itself, rather than on `status === 'RUNNING'`, means an in-between status this component
// hasn't been told about still renders no failure block instead of a fabricated one.
function hasTerminalOutcome(outcome: string | null): boolean {
	return outcome === 'SUCCEEDED' || outcome === 'FAILED' || outcome === 'CANCELLED';
}

// Resolves the id the record saved into the name a person should read. Three distinct cases, all
// handled honestly rather than by rendering blank:
//  - the agent is still in the viewer's available list: use its current title.
//  - the agent id is set but absent from that list: an agent a user cannot run is omitted from the
//    list rather than flagged, so absence cannot distinguish "un-deployed since this run" from any
//    other reason it is missing - the raw id is the only thing this side can still show truthfully.
//  - no agent id was saved on the run at all: state that plainly instead of showing nothing.
function resolveAgentTitle(agentId: string | null, formatMessage: ReturnType<typeof useIntl>['formatMessage']): string {
	if (!agentId) return formatMessage({id: 'nova_thread.turn.unknown_agent'});
	return getAvailableAgents().find(agent => agent.id === agentId)?.title ?? agentId;
}

// One saved run, rendered as a turn: the question that produced it (when the run saved one - an
// opening turn's prompt is carried entirely by the per-run input, so it has no separate question to
// show), then Nova's answer, attributed to the agent that produced it.
export const NovaConversationTurn = (props: NovaConversationTurnProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {run} = props;
	// Whether this card's copy control is currently saying it copied. Held here rather than
	// announced through a toast: the confirmation belongs on the control that was pressed, because a
	// long thread can hold several answers and a floating message would not say which one was taken.
	const [hasCopied, setHasCopied] = useState(false);
	const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clears the pending revert on unmount - the modal closing mid-confirmation would otherwise land
	// a state write on a component that no longer exists.
	useEffect(() => {
		return () => {
			if (copiedTimeoutRef.current !== null) clearTimeout(copiedTimeoutRef.current);
		};
	}, []);

	const answerText = run.answer;
	const handleCopy = useCallback(() => {
		if (!answerText) return;
		// Optional-chained, and the confirmation is deferred until the write actually resolves: the
		// clipboard API is absent outside a secure context and can be refused by permission, and a
		// control that says "Copied" over an empty clipboard is worse than one that says nothing.
		//
		// The rejection is caught rather than left to float. writeText rejects on a denied
		// permission and on a document that does not hold focus, and an uncaught rejection there is
		// an unhandled promise rejection - a console error in production, and a full-screen overlay
		// in a dev build - over something the control can simply decline to confirm. Warned rather
		// than swallowed silently, since nothing else records that the copy did not happen.
		void navigator.clipboard
			?.writeText(answerText)
			.then(() => {
				setHasCopied(true);
				if (copiedTimeoutRef.current !== null) clearTimeout(copiedTimeoutRef.current);
				copiedTimeoutRef.current = setTimeout(() => setHasCopied(false), COPIED_LABEL_MS);
			})
			.catch((error: unknown) => {
				// eslint-disable-next-line no-console
				console.warn('the answer could not be copied to the clipboard:', error);
			});
	}, [answerText]);

	const agentTitle = resolveAgentTitle(run.agentId, formatMessage);
	// One summary row stands in for the whole folded thread, so everything below the header is
	// withheld — not merely the answer text, but the sources, the failure block and every control.
	const isCollapsed = !!props.collapsed;
	const answered = isAnswered(run);

	return (
		<TurnStack data-cy={isCollapsed ? 'nova-thread-collapsed-row' : `nova-thread-turn-${run.id}`}>
			{props.replyText && (
				<QuestionRow>
					<QuestionAvatarSlot>
						<QuestionAvatar data-cy="nova-thread-reply-mark">
							{/* aria-hidden: the card's header names the author in words right beside this. */}
							<svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
								<circle cx="12" cy="8.5" r="3.3" />
								<path d="M5.5 19.5c0-3.3 2.9-5.2 6.5-5.2s6.5 1.9 6.5 5.2" strokeLinecap="round" />
							</svg>
						</QuestionAvatar>
					</QuestionAvatarSlot>
					<QuestionCard data-cy="nova-thread-question">
						<QuestionHeader>
							<QuestionAuthor>{formatMessage({id: 'nova_thread.turn.reply_author'})}</QuestionAuthor>
							<ReplyTag>{formatMessage({id: 'nova_thread.turn.reply_tag'})}</ReplyTag>
						</QuestionHeader>
						<Text>{props.replyText}</Text>
					</QuestionCard>
				</QuestionRow>
			)}
			<AnswerRow data-cy="nova-thread-answer">
				<NovaMarkImage src={novaMark} alt="" data-cy="nova-thread-nova-mark" />
				<AnswerCard isCollapsed={isCollapsed}>
					<HeaderRow
						isCollapsed={isCollapsed}
						onClick={isCollapsed ? props.onExpand : undefined}
						data-cy="nova-thread-answer-header"
					>
						{(props.onCollapse || isCollapsed) && (
							<CollapseToggle
								type="button"
								isCollapsed={isCollapsed}
								onClick={isCollapsed ? props.onExpand : props.onCollapse}
								aria-expanded={!isCollapsed}
								aria-label={formatMessage({
									id: isCollapsed ? 'nova_thread.collapse.expand_action' : 'nova_thread.collapse.action',
								})}
								data-cy="nova-thread-collapse-toggle"
							>
								<Icon icon="chevronDown" size="s" aria-hidden="true" />
							</CollapseToggle>
						)}
						<Detail data-cy="nova-thread-brand">
							<BrandName>{formatMessage({id: 'nova_thread.collapse.brand_label'})}</BrandName>
						</Detail>
						{/* The agent, named on its own chip and nothing more. The disclosure's optional
						    "why" line is left off deliberately: there is no automatic routing and no
						    account default, so every run is one the person chose, and a sentence saying
						    so under every answer in a long thread states the same thing over and over. */}
						<AgentSlot>
							<RoutingDisclosure agentName={agentTitle} />
						</AgentSlot>
						{run.ranAt && <RunTimestamp ranAt={run.ranAt} data-cy="nova-thread-time" />}
						{isCollapsed && (
							<>
								<HiddenNote data-cy="nova-thread-collapsed-count">{props.hiddenNote}</HiddenNote>
								<ShowAction data-cy="nova-thread-collapsed-show">
									{formatMessage({id: 'nova_thread.collapse.expand_action'})}
								</ShowAction>
							</>
						)}
					</HeaderRow>
					{!isCollapsed && answered && (
						<AnswerText data-cy="nova-thread-answer-text">
							<Text>{run.answer}</Text>
						</AnswerText>
					)}
					{!isCollapsed && props.changeAgentAffordance && (
						<CardRow data-cy="nova-thread-change-agent">{props.changeAgentAffordance}</CardRow>
					)}
					{!isCollapsed && answered && props.showGrounding && <BasedOnList items={basedOnItemsFor(run.grounding)} />}
					{!isCollapsed && hasTerminalOutcome(run.outcome) && <FailurePresentation outcome={outcomeFor(run)} />}
					{!isCollapsed && (answered || props.onDiscard) && (
						<CardRow data-cy="nova-thread-actions">
							{/* Only offered where there is an answer to put on the clipboard. A failed
							    turn has nothing to copy, and a control that copies an empty string is
							    indistinguishable from one that is broken. */}
							{answered && (
								<NovaCompactButton onPress={handleCopy} data-cy="nova-thread-copy">
									{formatMessage({
										id: hasCopied ? 'nova_thread.actions.copied' : 'nova_thread.actions.copy',
									})}
								</NovaCompactButton>
							)}
							{props.onDiscard && (
								<DiscardSlot>
									<NovaCompactButton
										tone="neutral"
										onPress={props.onDiscard}
										disabled={props.discardDisabled}
										title={props.discardDisabled ? props.discardDisabledReason : undefined}
										data-cy="nova-thread-discard"
									>
										{props.discardLabel ?? formatMessage({id: 'nova_thread.actions.discard'})}
									</NovaCompactButton>
								</DiscardSlot>
							)}
						</CardRow>
					)}
				</AnswerCard>
			</AnswerRow>
		</TurnStack>
	);
};
